import { StateCreator } from "zustand";
import { GlobalState } from "../globalStore";
import type { Profile, UserPreferences } from "@/core/fastapiClient/sdk";

// Use in the the app where needed
export const userProfileStateInitData: Profile = {
  id: "",
  latest_at: null, // Initialize with empty profile data
  roles: [],
  preferences: {},
};

export interface UserProfileState {
  data: Profile;
  isLoading: boolean;
  error: string | null;
}

export interface UserProfileSlice {
  userProfile: UserProfileState;
  setUserProfile: (profile: Profile) => void;
  setUserProfileLoading: () => void;
  setUserProfileError: (error: string) => void;
  resetUserProfile: () => void;
  updateUserProfile: (profile: Partial<Profile>) => Profile; // Add the new method
  updateUserProfilePreferences: (
    preferences: UserPreferences
  ) => UserPreferences; // Add new method

  // Initial state loading
  initFetchedUserProfile: boolean;
  setInitFetchedUserProfile: (fetched: boolean) => void;
}

export const createUserProfileSlice: StateCreator<
  GlobalState,
  [],
  [],
  UserProfileSlice
> = (set, get) => ({
  //* Basic state manipulation
  userProfile: {
    data: userProfileStateInitData, // Initialize with empty profile data
    isLoading: false,
    error: null,
  },
  setUserProfile: (profile) =>
    set({
      userProfile: { data: profile, isLoading: false, error: null },
    }), // Note the difference: Direct assignment to the state object, completely replacing it
  setUserProfileLoading: () =>
    set((state) => ({
      userProfile: { ...state.userProfile, isLoading: true, error: null },
    })), // Note the difference: Using a function to update the state object, merging the
  // new data with the existing state ... because loading state needs to know about the previous state
  setUserProfileError: (error) =>
    set((state) => ({
      userProfile: { ...state.userProfile, error, isLoading: false },
    })),
  resetUserProfile: () =>
    set({
      userProfile: {
        data: { id: "", latest_at: null, roles: [], preferences: {} },
        isLoading: false,
        error: null,
      },
    }),
  updateUserProfile: (profileUpdate) => {
    // Store the current profile before updating
    const previousProfile = { ...get().userProfile.data };

    // Update the profile state with merged data
    set((state) => ({
      userProfile: {
        ...state.userProfile,
        data: {
          ...state.userProfile.data,
          ...profileUpdate,
        },
        isLoading: false,
        error: null,
      },
    }));

    // Return the previous profile state
    return previousProfile;
  },
  updateUserProfilePreferences: (preferencesUpdate) => {
    // Store current preferences before updating
    const previousPreferences = { ...get().userProfile.data.preferences };

    // Update only the preferences part of the profile
    set((state) => ({
      userProfile: {
        ...state.userProfile,
        data: {
          ...state.userProfile.data,
          preferences: {
            ...state.userProfile.data.preferences,
            ...preferencesUpdate,
          },
        },
        isLoading: false,
        error: null,
      },
    }));

    // Return the previous preferences
    return previousPreferences;
  },
  // Initial state loading
  initFetchedUserProfile: false,
  setInitFetchedUserProfile: (fetched) =>
    set({ initFetchedUserProfile: fetched }),
});
